/**
 * OIDC fragment handoff.
 *
 * The case-service OIDC callback redirects the browser to the
 * post-login URL with the JWT in the URL fragment, e.g.:
 *
 *   /admin/dashboard#access_token=…&refresh_token=…&expires_in=900
 *
 * Fragments are not transmitted to the server, so a JWT in the
 * fragment is safe in transit. This module runs at app boot — if
 * a fragment with the expected keys is present, it parses + persists
 * via the existing auth-tokens store + strips the fragment from
 * the visible URL.
 *
 * Why a fragment + not a cookie:
 *   - The platform's JWTMiddleware (Go) reads
 *     `Authorization: Bearer <token>` headers ONLY; cookies are
 *     deliberately ignored.
 *   - The React API client pulls the bearer from localStorage.
 *   - The OIDC callback runs server-side, so a localStorage write
 *     can't happen there. The fragment is the bridge.
 */

import { setTokens } from './auth-tokens'

/**
 * Run once at app boot, BEFORE the React tree mounts. Idempotent —
 * a missing or unrecognised fragment is a no-op.
 */
export function consumeOIDCTokenFragment(): void {
  if (typeof window === 'undefined') return
  const hash = window.location.hash
  if (!hash || hash.length < 2) return

  // Fragment shape: '#access_token=…&refresh_token=…&expires_in=…'
  // (oauth-implicit-flow style). Tolerate a leading '#'.
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash)
  const accessToken = params.get('access_token')
  const refreshToken = params.get('refresh_token')
  const expiresInStr = params.get('expires_in')
  if (!accessToken || !refreshToken || !expiresInStr) return

  const expiresIn = Number.parseInt(expiresInStr, 10)
  if (!Number.isFinite(expiresIn) || expiresIn <= 0) return

  setTokens({
    access_token: accessToken,
    refresh_token: refreshToken,
    expires_in: expiresIn,
  })

  // Strip the fragment from the URL so a refresh doesn't try to
  // re-consume an already-stored token + so the URL bar doesn't
  // show a JWT to anyone watching over the analyst's shoulder.
  // history.replaceState avoids a navigation + preserves the
  // canonical path/search.
  const cleanURL =
    window.location.pathname +
    window.location.search /* preserves '?next=…' if one was passed through */
  window.history.replaceState(null, '', cleanURL || '/')
}
